<template>
    <div v-if="!loading && brandLinks && brandLinks.length > 0"
         class="bg-white p-20 pb-5 text-13">
        <h3 class="font-medium text-16 mb-15">
            {{ labelHeading }}
        </h3>
        <template v-for="brand in brandLinks">
            <CultureAwareRouterLink v-if="brand"
                                    :key="brand.url"
                                    :to="brand.url"
                                    class="theme-text-linkcolor underline hover:no-underline mr-40 mb-15">
                {{ brand.displayName }}
            </CultureAwareRouterLink>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Api from '@/project/http/Api.service';
import { BrandSearchPages } from '@/project/http/controllers/brandController';
import dictionaryService from '@/core/translation/dictionary.service';

const props = defineProps({
    brandName: { type: String, required: false, default: null },
    activeBrandCategory: { type: String, required: false, default: '' }
});

const loading = ref<boolean>(true);
const brandLinksData = ref<BrandSearchPages | null>(null);
const categoryDisplayNames = ref(new Map<string, string>());

const brandLinks = computed(() => {
    return (!loading.value && brandLinksFiltered.value?.map(link => {
        if (!link.categoryIds?.length) return null;
        const index = link.categoryIds[0].toString();
        const name = categoryDisplayNames.value.get(index);
        return {
            ...link,
            displayName: name
        };
    })) || null;
});

const brandLinksFiltered = computed(() => {
    if (brandLinksData.value?.links && brandLinksData.value.links.length > 0) {
        return (props.activeBrandCategory ? brandLinksData.value.links.filter(c => !c.url.endsWith(props.activeBrandCategory)) : brandLinksData.value.links);
    }
    return null;
});

const labelHeading = computed(():string => {
    return dictionaryService.get(props.activeBrandCategory ? 'Brand.BrandOverview.BrandCategories.OtherCategories' : 'Brand.BrandOverview.BrandCategories', [brandLinksData.value?.brandName || '']);
});

async function loadBrandLinks() {
    if (props.brandName) {
        brandLinksData.value = await Api.brand.brandSearchPages(props.brandName);
    } else {
        brandLinksData.value = null;
    }
}

async function loadCategoryNamesByIds(categoryIds: string[]) {
    const categories = await Api.navigation.categories(categoryIds);
    if (categories?.names) {
        Object.keys(categories.names).forEach((categoryId) => {
            categoryDisplayNames.value.set(categoryId, categories.names[categoryId]);
        });
    }
}

    async function loadCategoryNames() {
        let categories: string[] = [];
        if (brandLinksFiltered.value?.length) {
            brandLinksFiltered.value.forEach(cat => {
                if (cat.displayName) {
                    const firstCategoryId = cat.categoryIds[0];
                    categoryDisplayNames.value.set(firstCategoryId, cat.displayName);
                } else {
                    categories = categories.concat(cat.categoryIds);
                }
            });
        }
        if (categories.length) {
            const uniqueCategories = categories.filter((e, i, a) => a.indexOf(e) === i);
            await loadCategoryNamesByIds(uniqueCategories);
        }
    }
watch(
    () => props.brandName,
    async() => {
        loading.value = true;
        await loadBrandLinks();
        await loadCategoryNames();
        loading.value = false;
    },
    { immediate: true });
</script>
